import apiClient from './apiClient';
import {
  SettingDefinition,
  CreateSettingDefinitionRequest,
  UpdateSettingDefinitionRequest,
  SettingCategory,
  SettingDataType
} from '../types/SettingDefinition';

export const settingDefinitionApi = {
  /**
   * Get all setting definitions
   */
  getAll: async (includeInactive: boolean = false): Promise<SettingDefinition[]> => {
    const response = await apiClient.get<SettingDefinition[]>('/api/settingDefinitions', {
      params: { includeInactive }
    });
    return response.data;
  },

  /**
   * Get setting definitions by category
   */
  getByCategory: async (category: string): Promise<SettingDefinition[]> => {
    const response = await apiClient.get<SettingDefinition[]>(`/api/settingDefinitions/category/${category}`);
    return response.data;
  },

  /**
   * Get a single setting definition by key
   */
  getByKey: async (settingKey: string): Promise<SettingDefinition> => {
    const response = await apiClient.get<SettingDefinition>(`/api/settingDefinitions/${encodeURIComponent(settingKey)}`);
    return response.data;
  },

  /**
   * Get the category catalog (SettingCategory table in master)
   */
  getCategories: async (includeInactive: boolean = false): Promise<SettingCategory[]> => {
    const response = await apiClient.get<SettingCategory[]>('/api/settingDefinitions/categories', {
      params: { includeInactive }
    });
    return response.data;
  },

  /**
   * Create a new category in the catalog
   */
  createCategory: async (category: SettingCategory): Promise<SettingCategory> => {
    const response = await apiClient.post<SettingCategory>('/api/settingDefinitions/categories', category);
    return response.data;
  },

  /**
   * Update a category (display name/order/active — the key is immutable)
   */
  updateCategory: async (categoryKey: string, category: SettingCategory): Promise<SettingCategory> => {
    const response = await apiClient.put<SettingCategory>(
      `/api/settingDefinitions/categories/${encodeURIComponent(categoryKey)}`,
      category
    );
    return response.data;
  },

  /**
   * Get available data types
   */
  getDataTypes: async (): Promise<SettingDataType[]> => {
    const response = await apiClient.get<SettingDataType[]>('/api/settingDefinitions/data-types');
    return response.data;
  },

  /**
   * Create a new setting definition
   */
  create: async (request: CreateSettingDefinitionRequest): Promise<SettingDefinition> => {
    const response = await apiClient.post<SettingDefinition>('/api/settingDefinitions', request);
    return response.data;
  },

  /**
   * Update an existing setting definition
   */
  update: async (settingKey: string, request: UpdateSettingDefinitionRequest): Promise<SettingDefinition> => {
    const response = await apiClient.put<SettingDefinition>(
      `/api/settingDefinitions/${encodeURIComponent(settingKey)}`,
      request
    );
    return response.data;
  },

  /**
   * Delete a setting definition (hard delete)
   */
  delete: async (settingKey: string): Promise<void> => {
    await apiClient.delete(`/api/settingDefinitions/${encodeURIComponent(settingKey)}`);
  },

  /**
   * Deactivate a setting definition (soft delete)
   */
  deactivate: async (settingKey: string): Promise<void> => {
    await apiClient.post(`/api/settingDefinitions/${encodeURIComponent(settingKey)}/deactivate`);
  },

  /**
   * Activate a deactivated setting definition
   */
  activate: async (settingKey: string): Promise<void> => {
    await apiClient.post(`/api/settingDefinitions/${encodeURIComponent(settingKey)}/activate`);
  }
};

export default settingDefinitionApi;
